import type { CalloutTone } from '~components/Callout';

// Compared in whole cents, as the API compares (rules.mjs OVER_ALLOCATED_PRECISION), so a fraction of a cent left
// by adding amounts up never shows as over (TAV-3459, TAV-3463)
const CENTS_PER_DOLLAR = 100;
export const toCents = (amount: number): number => Math.round(amount * CENTS_PER_DOLLAR);
const toDollars = (cents: number): number => cents / CENTS_PER_DOLLAR || 0;

export interface BookedClaimed {
  booked: number;
  claimed: number;
}
/** Work beyond a limit, claimed first (rule 2): claimed counts against the limit before booked. */
export interface OverLimit {
  booked: number;
  claimed: number;
  total: number;
}
/** The same work within the limit: what a ring draws (rule 5). */
export interface WithinLimit {
  booked: number;
  claimed: number;
  unused: number;
}

const centsOf = (limit: number, { booked, claimed }: BookedClaimed) => ({
  limit: Math.max(0, toCents(limit)),
  booked: Math.max(0, toCents(booked)),
  claimed: Math.max(0, toCents(claimed)),
});

export const splitOverLimit = (limit: number, figures: BookedClaimed): OverLimit => {
  const cents = centsOf(limit, figures);
  const claimedOver = Math.max(0, cents.claimed - cents.limit);
  const totalOver = Math.max(0, cents.claimed + cents.booked - cents.limit);
  return { booked: toDollars(totalOver - claimedOver), claimed: toDollars(claimedOver), total: toDollars(totalOver) };
};

export const withinLimit = (limit: number, figures: BookedClaimed): WithinLimit => {
  const cents = centsOf(limit, figures);
  const claimed = Math.min(cents.claimed, cents.limit);
  const booked = Math.min(cents.booked, cents.limit - claimed);
  return { booked: toDollars(booked), claimed: toDollars(claimed), unused: toDollars(cents.limit - claimed - booked) };
};

/** How far one amount is beyond another, to the cent; zero when within it (an agreed budget over its funding). */
export const amountBeyond = (amount: number, limit: number): number =>
  toDollars(Math.max(0, toCents(amount) - toCents(limit)));

export type OverTone = Extract<CalloutTone, 'danger' | 'warning'>;

export const mostSeriousTone = (tones: OverTone[]): OverTone | null => {
  if (tones.length === 0) return null;
  return tones.includes('danger') ? 'danger' : 'warning';
};

/** Over-limit amounts added up, with how many budgets are over in each part (TAV-3463). */
export interface OverTotals extends OverLimit {
  budgets: number;
  bookedBudgets: number;
  claimedBudgets: number;
}

/** Adds up over-limit splits in whole cents, so the sums never carry a fraction of a cent. */
export const addOverLimits = (overs: OverLimit[]): OverTotals => {
  // Cents while adding
  const totals = overs.reduce<OverTotals>(
    (sum, over) => ({
      booked: sum.booked + toCents(over.booked),
      claimed: sum.claimed + toCents(over.claimed),
      total: sum.total + toCents(over.total),
      budgets: sum.budgets + (over.total > 0 ? 1 : 0),
      bookedBudgets: sum.bookedBudgets + (over.booked > 0 ? 1 : 0),
      claimedBudgets: sum.claimedBudgets + (over.claimed > 0 ? 1 : 0),
    }),
    { booked: 0, claimed: 0, total: 0, budgets: 0, bookedBudgets: 0, claimedBudgets: 0 },
  );
  return {
    ...totals,
    booked: toDollars(totals.booked),
    claimed: toDollars(totals.claimed),
    total: toDollars(totals.total),
  };
};
